import {
  HOLDING_CONFIDENCES,
  INTERACTION_TYPE_LABELS,
  INTERACTION_TYPES,
  ORGANIZATION_TYPE_LABELS,
  PRODUCT_HOLDING_STATUS_HELP,
  PRODUCT_HOLDING_STATUS_LABELS,
  PRODUCT_HOLDING_STATUSES,
  HOLDING_CONFIDENCE_LABELS,
  formatDate,
  formatNumber,
  type InteractionDto,
  type OrganizationDto,
  type ProductHoldingDto,
} from '@nova/shared';
import { CalendarClock, ExternalLink, MapPinned, MessageSquarePlus, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/Button.js';
import { Drawer } from '../../components/Drawer.js';
import { SkeletonPanel } from '../../components/Skeleton.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { cn } from '../../lib/cn.js';
import { ApiError } from '../../lib/api.js';
import { KommuneNeighboursMap, dominantStatusOf } from './KommuneNeighboursMap.js';
import { ProductMultiSelect } from './ProductMultiSelect.js';
import {
  DataQualityBadges,
  ExpiryBadge,
  Field,
  HoldingStatusBadge,
  SelectInput,
  TextArea,
  TextInput,
} from './marketWidgets.js';
import {
  useCreateInteraction,
  useMarketProducts,
  useOrganization,
  useOrgNeighbours,
  useUpsertHolding,
  type HoldingInput,
  type InteractionInput,
} from './useMarket.js';
import { AssessmentPanel } from './AssessmentPanel.js';

export function OrganizationDetail({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { data: org, isLoading } = useOrganization(id);
  return (
    <Drawer
      open={Boolean(id)}
      onClose={onClose}
      title={org ? org.name : 'Organisasjon'}
      subtitle={org ? ORGANIZATION_TYPE_LABELS[org.type] : undefined}
    >
      {isLoading || !org ? <SkeletonPanel lines={6} /> : <OrgBody org={org} />}
    </Drawer>
  );
}

function OrgBody({ org }: { org: OrganizationDto }) {
  const canEdit = useAuth().hasModule('SALES', 'MEMBER');

  return (
    <div className="space-y-6">
      {/* Meta */}
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <Fact label="Fylke" value={org.fylke ?? '—'} />
        <Fact label="Folketall" value={org.population ? formatNumber(org.population) : '—'} />
        <Fact label="Kommunenummer" value={org.kommunenummer ?? '—'} />
        <Fact label="Org.nr" value={org.orgNr ?? '—'} />
        <Fact label="Marked" value={org.marketName ?? '—'} />
        <div>
          <dt className="label-micro text-ink-soft">Nettside</dt>
          <dd className="mt-0.5">
            {org.website ? (
              <a
                href={org.website}
                target="_blank"
                rel="noreferrer noopener"
                className="text-accent inline-flex items-center gap-1"
              >
                Åpne <ExternalLink size={12} aria-hidden />
              </a>
            ) : (
              <span className="text-ink">—</span>
            )}
          </dd>
        </div>
      </dl>

      <HoldingsSection org={org} canEdit={canEdit} />
      {org.kommunenummer && <NeighboursSection org={org} />}
      <AssessmentPanel org={org} />
      <InteractionsSection org={org} canEdit={canEdit} />
    </div>
  );
}

// ── Produktbeholdning ─────────────────────────────────────────────────────────

function HoldingsSection({ org, canEdit }: { org: OrganizationDto; canEdit: boolean }) {
  const { data: products = [] } = useMarketProducts();
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const heldProductIds = new Set(org.holdings.map((h) => h.productId));
  const availableProducts = products.filter((p) => !heldProductIds.has(p.id));

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="label-micro text-ink-soft">Produktbeholdning</p>
        {canEdit && availableProducts.length > 0 && !adding && (
          <button
            className="text-accent inline-flex items-center gap-1 text-xs"
            onClick={() => {
              setAdding(true);
              setEditingProductId(null);
            }}
          >
            <Plus size={13} aria-hidden /> Legg til
          </button>
        )}
      </div>

      {org.holdings.length === 0 && !adding && (
        <p className="text-ink-soft text-xs">Ingen registrert beholdning ennå.</p>
      )}

      {org.holdings.map((h) =>
        editingProductId === h.productId ? (
          <HoldingForm
            key={h.productId}
            orgId={org.id}
            productId={h.productId}
            productName={h.productName}
            initial={h}
            onDone={() => setEditingProductId(null)}
          />
        ) : (
          <HoldingCard
            key={h.productId}
            holding={h}
            canEdit={canEdit}
            onEdit={() => {
              setEditingProductId(h.productId);
              setAdding(false);
            }}
          />
        ),
      )}

      {adding && availableProducts[0] && (
        <HoldingForm
          orgId={org.id}
          products={availableProducts}
          onDone={() => setAdding(false)}
        />
      )}
    </section>
  );
}

function HoldingCard({
  holding,
  canEdit,
  onEdit,
}: {
  holding: ProductHoldingDto;
  canEdit: boolean;
  onEdit: () => void;
}) {
  return (
    <div className="border-hairline bg-base/30 rounded-lg border p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-ink text-sm font-medium">{holding.productName}</span>
            <HoldingStatusBadge status={holding.status} />
            {holding.isSeed && <span className="text-ink-soft text-[10px]">demo</span>}
          </div>
          <p className="text-ink-soft mt-0.5 text-xs">{PRODUCT_HOLDING_STATUS_HELP[holding.status]}</p>
        </div>
        {canEdit && (
          <button className="text-ink-soft hover:text-ink shrink-0" onClick={onEdit} title="Rediger">
            <Pencil size={14} aria-hidden />
          </button>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
        {holding.competitor && (
          <span className="text-ink-soft text-xs">
            Dagens løsning: <span className="text-ink">{holding.competitor}</span>
          </span>
        )}
        <ExpiryBadge
          contractEnd={holding.contractEnd}
          daysToExpiry={holding.daysToExpiry}
          formatted={holding.contractEnd ? formatDate(holding.contractEnd) : ''}
        />
        {holding.annualValue != null && (
          <span className="text-ink-soft text-xs">
            Årlig verdi: <span className="text-ink">{formatNumber(holding.annualValue)} {holding.currency ?? 'NOK'}</span>
          </span>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <DataQualityBadges confidence={holding.confidence} liveVerified={holding.liveVerified} />
        {holding.source && (
          <span className="text-ink-soft/70 text-[11px]">
            Kilde: {holding.source}
            {holding.sourceDate ? ` · ${formatDate(holding.sourceDate)}` : ''}
          </span>
        )}
      </div>

      {holding.dataNote && (
        <p className="text-ink-soft/80 mt-1.5 text-[11px] leading-relaxed">{holding.dataNote}</p>
      )}
    </div>
  );
}

function HoldingForm({
  orgId,
  productId,
  productName,
  products,
  initial,
  onDone,
}: {
  orgId: string;
  productId?: string;
  productName?: string;
  products?: { id: string; name: string }[];
  initial?: ProductHoldingDto;
  onDone: () => void;
}) {
  const save = useUpsertHolding(orgId);
  const [pid, setPid] = useState(productId ?? products?.[0]?.id ?? '');
  const [status, setStatus] = useState<string>(initial?.status ?? 'PROSPEKT');
  const [competitor, setCompetitor] = useState(initial?.competitor ?? '');
  const [contractEnd, setContractEnd] = useState(initial?.contractEnd?.slice(0, 10) ?? '');
  const [contractStart, setContractStart] = useState(initial?.contractStart?.slice(0, 10) ?? '');
  const [annualValue, setAnnualValue] = useState(initial?.annualValue != null ? String(initial.annualValue) : '');
  const [confidence, setConfidence] = useState<string>(initial?.confidence ?? '');
  const [liveVerified, setLiveVerified] = useState(initial?.liveVerified ?? false);
  const [dataNote, setDataNote] = useState(initial?.dataNote ?? '');

  function submit() {
    const body: HoldingInput = {
      productId: pid,
      status,
      competitor: competitor.trim() || null,
      contractStart: contractStart || null,
      contractEnd: contractEnd || null,
      hasOptions: initial?.hasOptions ?? false,
      annualValue: annualValue.trim() ? Number(annualValue.replace(/\s/g, '')) : null,
      currency: annualValue.trim() ? (initial?.currency ?? 'NOK') : null,
      source: initial?.source ?? null,
      confidence: confidence || null,
      liveVerified,
      dataNote: dataNote.trim() || null,
    };
    save.mutate(body, { onSuccess: onDone });
  }

  return (
    <div className="border-accent/30 bg-base/40 space-y-3 rounded-lg border p-3">
      <p className="text-ink text-sm font-medium">
        {productName ?? 'Ny beholdning'}
      </p>
      {!productId && products && (
        <Field label="Produkt">
          <SelectInput value={pid} onChange={(e) => setPid(e.target.value)}>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </SelectInput>
        </Field>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Status">
          <SelectInput value={status} onChange={(e) => setStatus(e.target.value)}>
            {PRODUCT_HOLDING_STATUSES.map((s) => (
              <option key={s} value={s}>
                {PRODUCT_HOLDING_STATUS_LABELS[s]}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field label="Dagens løsning / konkurrent">
          <TextInput value={competitor} onChange={(e) => setCompetitor(e.target.value)} placeholder="f.eks. ACOS" />
        </Field>
        <Field label="Kontraktsstart">
          <TextInput type="date" value={contractStart} onChange={(e) => setContractStart(e.target.value)} />
        </Field>
        <Field label="Kontraktsutløp" hint="Viktigste enkeltopplysning.">
          <TextInput type="date" value={contractEnd} onChange={(e) => setContractEnd(e.target.value)} />
        </Field>
        <Field label="Årlig verdi (NOK)">
          <TextInput value={annualValue} onChange={(e) => setAnnualValue(e.target.value)} placeholder="valgfritt" inputMode="numeric" />
        </Field>
        <Field label="Konfidens">
          <SelectInput value={confidence} onChange={(e) => setConfidence(e.target.value)}>
            <option value="">— ikke satt —</option>
            {HOLDING_CONFIDENCES.map((c) => (
              <option key={c} value={c}>
                {HOLDING_CONFIDENCE_LABELS[c]}
              </option>
            ))}
          </SelectInput>
        </Field>
      </div>
      <label className="text-ink-soft flex items-center gap-2 text-sm">
        <input type="checkbox" checked={liveVerified} onChange={(e) => setLiveVerified(e.target.checked)} />
        Live-verifisert
      </label>
      <Field label="Notat">
        <TextArea rows={2} value={dataNote} onChange={(e) => setDataNote(e.target.value)} />
      </Field>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="primary" loading={save.isPending} disabled={!pid} onClick={submit}>
          Lagre
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Avbryt
        </Button>
        {save.isError && <span className="text-danger text-xs">{(save.error as ApiError).message}</span>}
      </div>
    </div>
  );
}

// ── Nabokommuner (lokalt kart) ──────────────────────────────────────────────

function NeighboursSection({ org }: { org: OrganizationDto }) {
  const navigate = useNavigate();
  const { data: products = [] } = useMarketProducts();
  const [productIds, setProductIds] = useState<string[]>([]);
  const { data, isLoading } = useOrgNeighbours(org.id, productIds);

  const selected = new Set(productIds);
  const centerHoldings = (productIds.length === 0
    ? org.holdings
    : org.holdings.filter((h) => selected.has(h.productId))
  ).map((h) => ({ productId: h.productId, productName: h.productName, status: h.status, competitor: h.competitor }));

  return (
    <section className="border-hairline space-y-3 border-t pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="label-micro text-ink-soft inline-flex items-center gap-1.5">
          <MapPinned size={13} aria-hidden /> Nabokommuner
        </p>
        <ProductMultiSelect products={products} selected={productIds} onChange={setProductIds} />
      </div>
      <p className="text-ink-soft/80 text-xs">
        Hvilke produkter nabokommunene har — til referansesalg og klynger. Klikk en nabo for å åpne den.
      </p>
      {isLoading ? (
        <SkeletonPanel lines={4} />
      ) : !data || data.neighbours.length === 0 ? (
        <p className="text-ink-soft text-xs">Fant ingen nabokommuner i kartet for denne kommunen.</p>
      ) : (
        <KommuneNeighboursMap
          centerCode={data.kommunenummer ?? org.kommunenummer ?? ''}
          centerName={org.name}
          centerStatus={dominantStatusOf(centerHoldings)}
          neighbours={data.neighbours}
          onOpenNeighbour={(id) => navigate(`/marked/${id}`)}
        />
      )}
    </section>
  );
}

// ── Oppfølging ────────────────────────────────────────────────────────────────

function InteractionsSection({ org, canEdit }: { org: OrganizationDto; canEdit: boolean }) {
  const [adding, setAdding] = useState(false);
  return (
    <section className="border-hairline space-y-3 border-t pt-4">
      <div className="flex items-center justify-between">
        <p className="label-micro text-ink-soft">Oppfølging</p>
        {canEdit && !adding && (
          <button className="text-accent inline-flex items-center gap-1 text-xs" onClick={() => setAdding(true)}>
            <MessageSquarePlus size={13} aria-hidden /> Logg oppfølging
          </button>
        )}
      </div>

      {adding && <InteractionForm orgId={org.id} products={org.holdings} onDone={() => setAdding(false)} />}

      {org.interactions.length === 0 && !adding ? (
        <p className="text-ink-soft text-xs">Ingen oppfølging logget ennå.</p>
      ) : (
        <ol className="space-y-2">
          {org.interactions.map((i) => (
            <InteractionRow key={i.id} interaction={i} />
          ))}
        </ol>
      )}
    </section>
  );
}

function InteractionRow({ interaction }: { interaction: InteractionDto }) {
  return (
    <li className="border-hairline bg-base/30 rounded-lg border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-ink text-sm font-medium">{INTERACTION_TYPE_LABELS[interaction.type]}</span>
        <span className="text-ink-soft text-[11px]">{formatDate(interaction.occurredAt)}</span>
      </div>
      <p className="text-ink-soft mt-1 text-xs leading-relaxed">{interaction.note}</p>
      {interaction.nextStep && (
        <p className="text-ink mt-1.5 flex items-start gap-1.5 text-xs">
          <CalendarClock size={12} className="mt-0.5 shrink-0" aria-hidden />
          <span>
            Neste steg: {interaction.nextStep}
            {interaction.nextStepDate ? ` (${formatDate(interaction.nextStepDate)})` : ''}
          </span>
        </p>
      )}
      {interaction.actorName && (
        <p className="text-ink-soft/60 mt-1 text-[10px]">
          {interaction.actorName}
          {interaction.productName ? ` · ${interaction.productName}` : ''}
        </p>
      )}
    </li>
  );
}

function InteractionForm({
  orgId,
  products,
  onDone,
}: {
  orgId: string;
  products: { productId: string; productName: string }[];
  onDone: () => void;
}) {
  const create = useCreateInteraction(orgId);
  const today = new Date().toISOString().slice(0, 10);
  const [type, setType] = useState<string>('MOTE');
  const [note, setNote] = useState('');
  const [occurredAt, setOccurredAt] = useState(today);
  const [nextStep, setNextStep] = useState('');
  const [nextStepDate, setNextStepDate] = useState('');
  const [productId, setProductId] = useState('');

  const uniqueProducts = Array.from(new Map(products.map((p) => [p.productId, p.productName])).entries());

  function submit() {
    const body: InteractionInput = {
      productId: productId || null,
      type,
      note: note.trim(),
      occurredAt,
      nextStep: nextStep.trim() || null,
      nextStepDate: nextStepDate || null,
    };
    create.mutate(body, { onSuccess: onDone });
  }

  return (
    <div className="border-accent/30 bg-base/40 space-y-3 rounded-lg border p-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Type">
          <SelectInput value={type} onChange={(e) => setType(e.target.value)}>
            {INTERACTION_TYPES.map((t) => (
              <option key={t} value={t}>
                {INTERACTION_TYPE_LABELS[t]}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field label="Dato">
          <TextInput type="date" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} />
        </Field>
      </div>
      {uniqueProducts.length > 0 && (
        <Field label="Produkt (valgfritt)">
          <SelectInput value={productId} onChange={(e) => setProductId(e.target.value)}>
            <option value="">Generelt</option>
            {uniqueProducts.map(([pid, name]) => (
              <option key={pid} value={pid}>
                {name}
              </option>
            ))}
          </SelectInput>
        </Field>
      )}
      <Field label="Notat">
        <TextArea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Hva skjedde?" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Neste steg (valgfritt)">
          <TextInput value={nextStep} onChange={(e) => setNextStep(e.target.value)} />
        </Field>
        <Field label="Neste steg-dato">
          <TextInput type="date" value={nextStepDate} onChange={(e) => setNextStepDate(e.target.value)} />
        </Field>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="primary" loading={create.isPending} disabled={!note.trim()} onClick={submit}>
          Lagre oppfølging
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Avbryt
        </Button>
        {create.isError && <span className="text-danger text-xs">{(create.error as ApiError).message}</span>}
      </div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="label-micro text-ink-soft">{label}</dt>
      <dd className={cn('text-ink mt-0.5')}>{value}</dd>
    </div>
  );
}
